import { TimelineDeposit, TimelinePayout } from './response.types'
import { BarRect, PointXY } from './types'

export const BAR_HEIGHT = 10
/** Высота линии точек депозитов. */
export const POINT_ROW_HEIGHT = 8
export const POINT_RADIUS = 3.5
/** Радиус точки-группы: в неё помещается счётчик. */
export const GROUP_RADIUS = 7
/** Депозиты одной суммы ближе этого по горизонтали склеиваются в группу, px. */
export const GROUP_DISTANCE = POINT_RADIUS * 2 + 2
/** Вертикальный зазор между объектами, которые пересекаются по времени. */
export const ROW_GAP = 3
/** Отступ бара слева и справа: бары, стыкующиеся по времени, не сливаются в полосу. */
export const BAR_INSET = 1.5

export type Frame = {
  fromMs: number
  toMs: number
  width: number
  baseHeight: number
  maxAmount: number
}

export type TimelineLayout = {
  bars: BarRect[]
  points: PointXY[]
  /** Полная высота полотна: растёт, когда объектам нужно место. */
  height: number
  /** Высота строки для суммы на оси — монотонна по сумме. */
  yOf: (amount: number) => number
}

type Placed = { left: number; right: number; y: number; height: number }

type Item =
  | {
      type: 'bar'
      amount: number
      order: number
      left: number
      right: number
      payout: TimelinePayout
      startMs: number
      endMs: number
    }
  | {
      type: 'point'
      amount: number
      order: number
      left: number
      right: number
      deposits: TimelineDeposit[]
      x: number
      radius: number
    }

/** Высота полосы индекса пересечений, px. */
const BAND = 16

const xOf = (ms: number, f: Frame) => ((ms - f.fromMs) / (f.toMs - f.fromMs)) * f.width

type DepositGroupXY = { amount: number; x: number; deposits: TimelineDeposit[] }

/**
 * Склеивает депозиты одной суммы, чьи точки легли бы друг на друга, в одну
 * группу. Группа тянется, пока следующий депозит ближе GROUP_DISTANCE к первому
 * в ней, так что группа не расползается на всё окно цепочкой соседей.
 */
export const groupDeposits = (deposits: TimelineDeposit[], f: Frame): DepositGroupXY[] => {
  const byAmount = new Map<number, { x: number; d: TimelineDeposit }[]>()
  deposits.forEach((d) => {
    const ms = Date.parse(d.at)
    if (ms < f.fromMs || ms >= f.toMs) return
    const list = byAmount.get(d.amount)
    const entry = { x: xOf(ms, f), d }
    if (list) list.push(entry)
    else byAmount.set(d.amount, [entry])
  })
  const groups: DepositGroupXY[] = []
  byAmount.forEach((list, amount) => {
    list.sort((a, b) => a.x - b.x)
    let start = 0
    for (let i = 1; i <= list.length; i++) {
      if (i < list.length && list[i].x - list[start].x < GROUP_DISTANCE) continue
      const chunk = list.slice(start, i)
      const x = chunk.reduce((s, e) => s + e.x, 0) / chunk.length
      groups.push({ amount, x, deposits: chunk.map((e) => e.d) })
      start = i
    }
  })
  return groups
}

/** Сколько объектов максимум пересекаются по горизонтали в одной точке. */
const peakOverlap = (items: Item[]): number => {
  const events: [number, number][] = []
  items.forEach((i) => {
    events.push([i.left, 1])
    events.push([i.right, -1])
  })
  events.sort((a, b) => a[0] - b[0] || a[1] - b[1])
  let cur = 0
  let peak = 0
  events.forEach(([, d]) => {
    cur += d
    peak = Math.max(peak, cur)
  })
  return peak
}

/**
 * Раскладывает бары выплат и точки депозитов по сумме сверху вниз.
 *
 * Высота шкалы сумм выбирается по пиковой одновременности: сколько объектов в
 * самый плотный момент окна живут одновременно, столько строк шкала обязана
 * вместить. Тогда объект, пересёкшийся с уже положенным по времени и по высоте,
 * опускается под него лишь на пару строк и остаётся рядом со своей суммой, а не
 * уезжает в зону чужих сумм. Точки депозитов живут на своих линиях и никогда не
 * ложатся поверх бара; объекты, не пересекающиеся по времени, делят одну линию.
 */
export const layoutTimeline = (
  payouts: TimelinePayout[],
  deposits: TimelineDeposit[],
  f: Frame
): TimelineLayout => {
  const items: Item[] = []
  payouts.forEach((p) => {
    const startMs = Date.parse(p.start)
    const endMs = p.end === null ? f.toMs : Date.parse(p.end)
    if (endMs <= f.fromMs || startMs >= f.toMs) return
    const left = xOf(Math.max(startMs, f.fromMs), f) + BAR_INSET
    const right = Math.max(xOf(Math.min(endMs, f.toMs), f) - BAR_INSET, left + 1)
    items.push({
      type: 'bar',
      amount: p.amount,
      order: startMs,
      left,
      right,
      payout: p,
      startMs,
      endMs,
    })
  })
  groupDeposits(deposits, f).forEach((g) => {
    const radius = g.deposits.length > 1 ? GROUP_RADIUS : POINT_RADIUS
    items.push({
      type: 'point',
      amount: g.amount,
      order: g.x,
      left: g.x - radius - 1,
      right: g.x + radius + 1,
      deposits: g.deposits,
      x: g.x,
      radius,
    })
  })
  // Bars before points of the same amount: the payout row comes first, its deposits right under it.
  items.sort(
    (a, b) =>
      b.amount - a.amount || (a.type === b.type ? a.order - b.order : a.type === 'bar' ? -1 : 1)
  )

  const scaleHeight = Math.max(f.baseHeight, peakOverlap(items) * (BAR_HEIGHT + ROW_GAP))
  const base = (amount: number) => (1 - amount / (f.maxAmount || 1)) * scaleHeight

  const bars: BarRect[] = []
  const points: PointXY[] = []
  const bands = new Map<number, Placed[]>()
  let lowest = 0

  const collide = (left: number, right: number, y: number, height: number): Placed | null => {
    const from = Math.floor((y - BAR_HEIGHT - ROW_GAP) / BAND)
    const to = Math.floor((y + height + ROW_GAP) / BAND)
    for (let band = from; band <= to; band++) {
      const list = bands.get(band)
      if (!list) continue
      for (const p of list) {
        if (
          left < p.right &&
          p.left < right &&
          y < p.y + p.height + ROW_GAP &&
          p.y < y + height + ROW_GAP
        )
          return p
      }
    }
    return null
  }

  for (const item of items) {
    const height = item.type === 'bar' ? BAR_HEIGHT : Math.max(POINT_ROW_HEIGHT, item.radius * 2)
    let y = base(item.amount)
    for (
      let hit = collide(item.left, item.right, y, height);
      hit;
      hit = collide(item.left, item.right, y, height)
    ) {
      y = hit.y + hit.height + ROW_GAP
    }
    const placed: Placed = { left: item.left, right: item.right, y, height }
    for (let band = Math.floor(y / BAND); band <= Math.floor((y + height) / BAND); band++) {
      const list = bands.get(band)
      if (list) list.push(placed)
      else bands.set(band, [placed])
    }
    lowest = Math.max(lowest, y + height)

    if (item.type === 'bar') {
      bars.push({
        payout: item.payout,
        x: item.left,
        y,
        width: item.right - item.left,
        clippedLeft: item.startMs < f.fromMs,
        clippedRight: item.payout.end === null || item.endMs > f.toMs,
      })
    } else {
      points.push({ deposits: item.deposits, x: item.x, y: y + height / 2, radius: item.radius })
    }
  }

  return { bars, points, height: Math.max(scaleHeight, lowest) + BAR_HEIGHT, yOf: base }
}

/** Круглые деления оси сумм от 0 до ближайшего «красивого» значения не ниже max. */
export const amountTicks = (max: number): number[] => {
  const safeMax = Math.max(max, 1)
  const raw = safeMax / 5
  const magnitude = 10 ** Math.floor(Math.log10(raw))
  const step = [1, 2, 5, 10].map((m) => m * magnitude).find((s) => s >= raw) ?? raw
  const ticks: number[] = []
  for (let v = 0; v < safeMax + step; v += step) ticks.push(v)
  return ticks
}
